import { useCallback, useEffect, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { uploadFile } from '../lib/upload';
import { useI18n } from '../lib/i18n';
import { FileDrop } from './FileDrop';

type InspFile = { id: string; file_name: string | null; for_inspector?: boolean };

/**
 * ADJUNTOS QUE EL INSPECTOR VE EN EL CELULAR, junto a las notas de la visita.
 *
 * Vivía metido dentro de la sección de Insurance inspection y no en el panel de "Notes for the
 * inspector" del claim, que es donde la gente ESCRIBE la nota. Resultado: escribían "te dejo la
 * foto de referencia del ATR" en un textarea sin adjuntos, la foto se quedaba en Files —donde la
 * app de campo no entra— y el inspector abría la visita y no veía nada. Gabriel lo reportó varias
 * veces y al medirlo había **0 archivos** marcados para el inspector en toda la producción: no es
 * que fallara el envío, es que no había dónde soltarlos.
 *
 * Por eso ahora es un componente aparte: el mismo cuadro va en los dos lugares.
 */
export function InspectorFilesBox({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [files, setFiles] = useState<InspFile[]>([]);
  const [subiendo, setSubiendo] = useState<string | null>(null);

  const cargar = useCallback(() => {
    api.get<{ files: InspFile[] }>(`/api/claims/${claimId}/files`)
      .then((r) => setFiles((r.files || []).filter((f) => f.for_inspector)))
      .catch(() => { /* best-effort: sin archivos no se rompe la nota */ });
  }, [claimId]);
  useEffect(() => { cargar(); }, [cargar]);

  async function subir(list: File[]) {
    for (const f of list) {
      setSubiendo(f.name);
      try {
        const key = await uploadFile(f, 'misc');
        // Queda en Files del claim como cualquier documento, pero marcado para que viaje con la
        // visita — no es una copia aparte que después nadie sepa dónde está.
        await api.post(`/api/claims/${claimId}/files`, {
          fileKey: key, fileName: f.name, mimeType: f.type || null, forInspector: true,
        });
      } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
    }
    setSubiendo(null);
    cargar();
  }

  /** Deja de mandárselo. El archivo NO se borra del claim: solo sale de la visita. */
  async function quitar(id: string) {
    try { await api.patch(`/api/files/${id}/for-inspector`, { on: false }); cargar(); } catch { /* ignore */ }
  }

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <FileDrop onFile={(f) => subir([f])} onFiles={subir} multiple accept="application/pdf,image/*"
        label={t('cwa.inspectorFilesDrop')} busy={!!subiendo} busyLabel={subiendo ?? ''} compact />
      {files.length > 0 && (
        <div style={{ display: 'grid', gap: 4 }}>
          {files.map((f) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-xs)', border: '1px solid var(--line)', borderRadius: 7, padding: '4px 8px' }}>
              <span><Paperclip size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name || `file-${f.id}`}</span>
              <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => quitar(f.id)} title={t('cwa.inspectorFileRemove')}><X size={12} strokeWidth={1.75} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
